import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  MenuItem,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescribedField from '../DescribedField';

const emptyMember = () => ({
  id: crypto.randomUUID(),
  professionalId: '',
  role: '',
});

/**
 * TeamBlockEditor — Editor do bloco Equipe (só company)
 * TODO 4.9
 *
 * Props:
 *   - value: { members: [{ id, professionalId, role }] }
 *   - onChange: (patch) => void
 *   - context: { availableProfessionals }
 */
const TeamBlockEditor = ({ value = {}, onChange, context = {} }) => {
  const { members = [] } = value;
  const { availableProfessionals = [] } = context;

  const patchMembers = (newMembers) => onChange({ ...value, members: newMembers });

  const addMember = () => patchMembers([...members, emptyMember()]);

  const removeMember = (index) => patchMembers(members.filter((_, i) => i !== index));

  const updateMember = (index, field, val) => {
    const updated = members.map((m, i) =>
      i === index ? { ...m, [field]: val } : m
    );
    patchMembers(updated);
  };

  // Ao selecionar o profissional, faz SNAPSHOT de nome/especialidade/foto no config do
  // bloco. O template é HTML estático e não resolve professionalId → precisa dos dados
  // denormalizados (mesmo padrão do bloco Serviços). professionalId fica guardado só
  // como referência. Limpar a seleção zera o snapshot.
  const selectProfessional = (index, professionalId) => {
    const prof = availableProfessionals.find((p) => p.id === professionalId);
    const updated = members.map((m, i) =>
      i === index
        ? {
            ...m,
            professionalId,
            name: prof?.name ?? '',
            specialty: prof?.specialty ?? '',
            imageUrl: prof?.photoUrl ?? '',
          }
        : m
    );
    patchMembers(updated);
  };

  return (
    <DescribedField
      label="Membros da equipe"
      description="Grade de profissionais do estabelecimento com foto, nome e função. Ajuda o visitante a identificar quem compõe a equipe antes de agendar. Nome e foto vêm do cadastro de Profissionais — para atualizar esses dados, edite o cadastro lá."
      helperLink={{ entity: 'professional' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {members.map((member, index) => (
          <Paper key={member.id ?? index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Membro {index + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => removeMember(index)}
                aria-label="Remover membro"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              select
              fullWidth
              label="Profissional"
              value={member.professionalId}
              onChange={(e) => selectProfessional(index, e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
              helperText="Profissional vinculado. Nome, foto e especialidade são copiados do cadastro ao selecionar."
            >
              <MenuItem value="">-- Selecione --</MenuItem>
              {availableProfessionals.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name || p.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Função na equipe (opcional)"
              value={member.role}
              onChange={(e) => updateMember(index, 'role', e.target.value)}
              size="small"
              helperText="Função dentro da equipe, se diferente da especialidade (ex.: 'Coordenadora clínica')."
            />
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addMember}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Adicionar membro
        </Button>
      </Box>
    </DescribedField>
  );
};

TeamBlockEditor.propTypes = {
  value: PropTypes.shape({
    members: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        professionalId: PropTypes.string,
        name: PropTypes.string,       // snapshot do cadastro
        specialty: PropTypes.string,  // snapshot do cadastro
        imageUrl: PropTypes.string,   // snapshot do cadastro (photoUrl)
        role: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.shape({
    availableProfessionals: PropTypes.array,
  }),
};

export default TeamBlockEditor;
